import { useIsUserRole } from "@/app/utils/auth";
import {
  ArrowPathIcon,
  ClockIcon,
  EyeIcon,
  EyeSlashIcon,
  FolderIcon,
  TrashIcon,
} from "@heroicons/react/16/solid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal, { openModal } from "../../components/modal";
import { useToastEffect } from "../../hooks";
import { useGetCourseQuery } from "../../services/courseApi";
import {
  useDeleteMaterialMutation,
  useGetMaterialsQuery,
  useLazyGetMaterialQuery,
  useRegenerateEmbeddingMutation,
  useUpdateMaterialStatusMutation,
} from "../../services/materialApi";

const MaterialSection = ({ courseId, onMaterialStatusChange }) => {
  const { t } = useTranslation(["coursePage", "toast", "header", "commons"]);
  const [isManaging, setIsManaging] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<string | undefined>(
    undefined,
  );

  const { data: course } = useGetCourseQuery(courseId ?? "");
  const isAdmin = useIsUserRole("admin");

  useEffect(() => {
    setIsManaging(course?.isManaging || isAdmin);
  }, [course?.isManaging, isAdmin]);

  const { data: materials } = useGetMaterialsQuery(courseId ?? "", {
    skip: !isManaging,
  });

  const [triggerGetMaterial, { data: materialDownloadURL }] =
    useLazyGetMaterialQuery();

  const [
    deleteMaterial,
    { isError: deleteIsError, isSuccess: deleteIsSuccess, error: deleteError },
  ] = useDeleteMaterialMutation();

  const [
    updateMaterialStatus,
    {
      isError: updateMaterialStatusIsError,
      isSuccess: updateMaterialStatusIsSuccess,
      error: updateMaterialStatusError,
    },
  ] = useUpdateMaterialStatusMutation();

  const [
    regnerateEmbedding,
    {
      isError: regenerateIsError,
      isLoading: regenerateIsLoading,
      isSuccess: regenerateIsSuccess,
      error: regenerateError,
    },
  ] = useRegenerateEmbeddingMutation();

  const downloadMaterial = (materialFilename: string) => {
    triggerGetMaterial({
      courseId: courseId ?? "",
      materialFilename,
    });
  };

  const clickDeleteMaterial = (material: string) => {
    setSelectedMaterial(material);
    openModal("delete_modal");
  };

  const clickRegenerateEmbedding = (material: string) => {
    setSelectedMaterial(material);
    openModal("regenerate_modal");
  };

  useEffect(() => {
    if (materialDownloadURL) {
      const link = document.createElement("a");
      link.href = materialDownloadURL.url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [materialDownloadURL]);

  useEffect(() => {
    if (materials && onMaterialStatusChange) {
      onMaterialStatusChange(materials.length > 0);
    }
  }, [materials, onMaterialStatusChange]);

  useToastEffect({
    isError: updateMaterialStatusIsError,
    isSuccess: updateMaterialStatusIsSuccess,
    error: updateMaterialStatusError,
    successMessage: t("coursePage.updateMaterialStatus", { ns: "toast" }),
  });

  useToastEffect({
    isError: regenerateIsError,
    isSuccess: regenerateIsSuccess,
    error: regenerateError,
    successMessage: t("coursePage.successEmbeddingRegenerate", { ns: "toast" }),
  });

  useToastEffect({
    isError: deleteIsError,
    isSuccess: deleteIsSuccess,
    error: deleteError,
    successMessage: t("coursePage.successMaterialDelete", { ns: "toast" }),
  });

  return (
    isManaging &&
    materials &&
    materials.length > 0 && (
      <>
        {materials.every((material) => !material.isActive) && (
          <div className="alert alert-warning">
            {t("warningNoMaterialsEnabled")}
          </div>
        )}
        <div className="collapse-arrow collapse mt-md mb-lg bg-neutral shadow-sm">
          <input type="checkbox" />
          <div className="collapse-title flex flex-row flex-wrap font-medium text-md">
            <FolderIcon className="mr-2 h-6 w-6" />
            {`${t("courseMaterial")} (${materials.length})`}
          </div>
          <div className="collapse-content">
            <div className="flex flex-col items-start gap-2">
              {materials
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((material) => (
                  <div
                    className="flex w-full flex-wrap items-center rounded-xl border-2 p-1 "
                    key={material.name}
                  >
                    <div className="flex">
                      <div
                        className="tooltip"
                        data-tip={t("modalDeleteMaterial")}
                      >
                        <button
                          className="btn btn-ghost btn-sm btn-circle"
                          onClick={() => clickDeleteMaterial(material.name)}
                          type="button"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>

                      <div
                        className="tooltip tooltip-right"
                        data-tip={t("tooltipRegenerateEmbeddings")}
                      >
                        <button
                          disabled={regenerateIsLoading || !material.isActive}
                          className="btn btn-ghost btn-sm btn-circle mr-2"
                          onClick={() =>
                            clickRegenerateEmbedding(material.name)
                          }
                          type="button"
                        >
                          <ArrowPathIcon
                            className={`h-5 w-5 ${regenerateIsLoading && selectedMaterial === material.name ? "animate-spin" : ""}`}
                          />
                        </button>
                      </div>

                      <div
                        className="tooltip"
                        data-tip={
                          material.isActive
                            ? t("tooltipDisableMaterial")
                            : t("tooltipEnableMaterial")
                        }
                      >
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm btn-circle"
                          onClick={() => {
                            updateMaterialStatus({
                              courseId: courseId ?? "",
                              materialFilename: material.name,
                              enabled: !material.isActive,
                            });
                          }}
                        >
                          {material.isActive ? (
                            <EyeIcon className="h-5 w-5" />
                          ) : (
                            <EyeSlashIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => downloadMaterial(material.name)}
                      type="button"
                    >
                      <div className="mr-2 flex flex-row items-center">
                        <span className="link max-w-3/4 break-words text-left">
                          {material.name}
                        </span>
                      </div>
                    </button>
                    <span className="flex gap-x-2 text-sm">
                      <div
                        className="tooltip"
                        data-tip={t("embeddingsCreated")}
                      >
                        <ClockIcon className="h-5 w-5" />
                      </div>
                      {material.embeddingCreated ? (
                        <span>
                          {Intl.DateTimeFormat("de-DE", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          }).format(new Date(material.embeddingCreated))}
                        </span>
                      ) : (
                        <div
                          className="tooltip"
                          data-tip={t("embeddingsCreatedDetails")}
                        >
                          {t("unknownEmbeddingCreationTimestamp")}
                        </div>
                      )}
                    </span>
                    <div className="flex flex-1 justify-end">
                      <span
                        className={`badge badge-sm mr-sm border-2 font-semibold ${material.isActive ? "border-neutral-content" : "border-base-300 text-base-content"}`}
                      >
                        {material.isActive
                          ? t("materialActiveBadge")
                          : t("materialInactiveBadge")}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <Modal
            id="delete_modal"
            title={t("modalDeleteMaterial")}
            content={t("modalDeleteMaterialContent", {
              fileName: selectedMaterial,
            })}
            onConfirm={() =>
              selectedMaterial &&
              deleteMaterial({
                courseId: courseId ?? "",
                materialFilename: selectedMaterial,
              })
            }
            onCancel={() => setSelectedMaterial(undefined)}
          />
          <Modal
            id="regenerate_modal"
            title={t("modalRegenerateEmbedding")}
            content={t("modalRegenerateEmbeddingContent", {
              fileName: selectedMaterial,
            })}
            onConfirm={() =>
              selectedMaterial &&
              regnerateEmbedding({
                courseId: courseId ?? "",
                materialFilename: selectedMaterial,
              })
            }
            onConfirmLabel={t("modalRegenerateEmbeddingConfirmLabel")}
            onCancel={() => setSelectedMaterial(undefined)}
          />
        </div>
      </>
    )
  );
};

export default MaterialSection;
